import { useState, type ReactNode } from 'react';
import { HbsError, inviteMember } from '../api/hbs';
import {
  ApiError,
  assignRole,
  createWorker,
  fetchMembers,
  fetchRoleAssignments,
  fetchWorkers,
  updateWorker,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { namaLayak } from '../lib/display-name';
import { presentError } from '../lib/error-messages';
import { hasAnyPermission } from '../lib/navigation';
import {
  candidateMembers,
  employmentStatusLabel,
  workerDisplayName,
} from '../lib/worker-candidates';
import {
  ROLE_CHOICES,
  assignableRoles,
  roleLabel,
  rolesOf,
  workersWithoutRole,
  type WorkforceRole,
} from '../lib/worker-roles';
import {
  MEMBERSHIP_ROLE_CHOICES,
  buildAcceptUrl,
  inviteProblem,
  type MembershipRole,
} from '../lib/invite';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Kelola Karyawan — menghubungkan anggota organisasi (HBS) dengan profil pekerja (Workforce),
 * DAN memberinya peran.
 *
 * ADA KARENA pilot 16 Agustus 2026: absensi seseorang tercatat benar di database, tetapi tidak
 * pernah muncul di dashboard — dashboard membaca roster pekerja, dan orangnya tidak ada di sana.
 *
 * PERAN ditambahkan setelah lubang kedua ditemukan saat menyiapkan onboarding: `TenantContextGuard`
 * menolak SETIAP permintaan dari orang tanpa satu pun baris `role_assignments`. Menambahkan
 * seseorang ke roster membuatnya muncul di dashboard, tetapi ia tetap TIDAK DAPAT MASUK. Dua hal
 * yang berbeda, dan sampai sekarang hanya salah satunya punya layar.
 *
 * Yang halaman ini SENGAJA tidak lakukan: membuat akun. Akun milik HBS. Aplikasi absensi lama
 * membuatnya sendiri karena ia satu-satunya aplikasi; di sini, membuat akun kedua untuk orang
 * yang sama akan memecah identitasnya menjadi dua yang tidak pernah bertemu.
 */
export function WorkerManagePage(): ReactNode {
  const session = useSession();
  const [pilihan, setPilihan] = useState('');
  const [peran, setPeran] = useState<WorkforceRole>('COURIER');
  const [nomor, setNomor] = useState('');
  const [jabatan, setJabatan] = useState('');
  const [mengirim, setMengirim] = useState(false);
  const [galat, setGalat] = useState<ApiError | null>(null);
  const [pesan, setPesan] = useState<string | null>(null);
  const [peringatan, setPeringatan] = useState<string | null>(null);
  /*
   * Baris mana yang sedang membuka pemilih peran.
   *
   * Pemilih TIDAK ditampilkan terus-menerus. Mengubah peran adalah tindakan yang jarang —
   * menaruh satu dropdown di setiap baris membuat daftar pekerja terbaca seperti formulir, dan
   * yang sering dilihat (siapa, perannya apa, aktif atau tidak) tenggelam di antara kontrol yang
   * hampir tidak pernah dipakai.
   */
  const [ubahPeranUntuk, setUbahPeranUntuk] = useState<string | null>(null);

  const bolehTambah = hasAnyPermission(session.me.permissions, ['workforce.worker.create']);
  const bolehBacaPeran = hasAnyPermission(session.me.permissions, ['workforce.role.read']);
  const bolehAturPeran = hasAnyPermission(session.me.permissions, ['workforce.role.assign']);

  const pekerja = useAsync(() => fetchWorkers(session.auth), [session.auth]);
  /*
   * Roster hanya diambil bila pemakainya memang boleh menambah pekerja.
   *
   * Bukan sekadar penghematan: daftar ini menyebut nama seluruh anggota organisasi, dan
   * memintanya untuk pengguna yang tidak akan pernah memakainya berarti memindahkan data itu ke
   * perangkat yang tidak membutuhkannya. Server tetap menolaknya sendiri (`worker.create`);
   * ini menghindari permintaan yang sudah pasti ditolak.
   */
  const anggota = useAsync(
    () => (bolehTambah ? fetchMembers(session.auth) : Promise.resolve([])),
    [session.auth, bolehTambah],
  );
  const peranan = useAsync(
    () => (bolehBacaPeran ? fetchRoleAssignments(session.auth) : Promise.resolve([])),
    [session.auth, bolehBacaPeran],
  );

  const daftarPekerja = pekerja.state.status === 'ready' ? pekerja.state.data : [];
  const daftarAnggota = anggota.state.status === 'ready' ? anggota.state.data : [];
  const daftarPeran = peranan.state.status === 'ready' ? peranan.state.data : [];
  const kandidat = candidateMembers(daftarAnggota, daftarPekerja);
  /*
   * Hanya dihitung bila daftar peran BENAR-BENAR terbaca. Bila permintaannya gagal, `daftarPeran`
   * kosong dan setiap pekerja akan tampak tidak punya peran — peringatan palsu yang menyuruh admin
   * memperbaiki sesuatu yang tidak rusak.
   */
  const tanpaPeran =
    peranan.state.status === 'ready' ? workersWithoutRole(daftarPekerja, daftarPeran) : [];

  async function tambah(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!pilihan) return;
    setMengirim(true);
    setGalat(null);
    setPesan(null);
    setPeringatan(null);
    try {
      /*
       * PERAN LEBIH DULU, baru profil pekerja — urutannya disengaja.
       *
       * Bila langkah kedua gagal, yang tersisa adalah orang yang DAPAT MASUK dan absensinya
       * tercatat, hanya belum muncul di dashboard. Urutan sebaliknya meninggalkan orang yang
       * ditolak di setiap layar — persis kegagalan yang layar ini dibuat untuk mengakhiri.
       */
      await assignRole(session.auth, { userId: pilihan, role: peran });
      try {
        await createWorker(session.auth, {
          userId: pilihan,
          ...(nomor.trim() ? { employeeNumber: nomor.trim() } : {}),
          ...(jabatan.trim() ? { jobTitle: jabatan.trim() } : {}),
        });
      } catch (sebab) {
        // Setengah berhasil dikatakan apa adanya. "Gagal" saja akan membuat admin mengulang dari
        // awal dan bingung ketika orangnya ternyata sudah bisa masuk.
        setPeringatan(
          `Peran ${roleLabel(peran)} berhasil diberikan — orang ini sudah dapat masuk. ` +
            'Profil pekerjanya BELUM tersimpan, jadi absensinya belum muncul di dashboard. ' +
            'Coba tambahkan lagi; pemberian peran tidak akan terduplikasi.',
        );
        throw sebab;
      }
      setPilihan('');
      setNomor('');
      setJabatan('');
      setPesan(
        `Pekerja ditambahkan sebagai ${roleLabel(peran)}. Ia sudah dapat masuk, dan absensinya ` +
          'akan muncul di dashboard.',
      );
      pekerja.reload();
      anggota.reload();
      peranan.reload();
    } catch (sebab) {
      if (sebab instanceof ApiError) setGalat(sebab);
    } finally {
      setMengirim(false);
    }
  }

  async function beriPeran(userId: string, role: WorkforceRole): Promise<void> {
    setGalat(null);
    setPesan(null);
    setPeringatan(null);
    try {
      await assignRole(session.auth, { userId, role });
      setPesan(
        `Peran sekarang ${roleLabel(role)}. Peran sebelumnya dicabut — satu orang satu peran.`,
      );
      peranan.reload();
    } catch (sebab) {
      if (sebab instanceof ApiError) setGalat(sebab);
    }
  }

  async function ubahStatus(id: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED') {
    setGalat(null);
    setPesan(null);
    setPeringatan(null);
    try {
      await updateWorker(session.auth, id, { employmentStatus: status });
      pekerja.reload();
    } catch (sebab) {
      if (sebab instanceof ApiError) setGalat(sebab);
    }
  }

  if (pekerja.state.status === 'loading') return <LoadingState label="Memuat daftar pekerja…" />;
  if (pekerja.state.status === 'error') {
    return <ErrorCard error={pekerja.state.error} onRetry={pekerja.reload} />;
  }

  return (
    <>
      {bolehTambah ? <UndangAnggota onSelesai={() => anggota.reload()} /> : null}
      {galat ? (
        <section className="card error">
          <h2>{presentError(galat.code, galat.status).title}</h2>
          <p className="muted small">{presentError(galat.code, galat.status).hint}</p>
        </section>
      ) : null}
      {peringatan ? (
        <section className="card">
          <p className="warning">{peringatan}</p>
        </section>
      ) : null}
      {pesan ? (
        <section className="card ok">
          <p>{pesan}</p>
        </section>
      ) : null}

      {tanpaPeran.length > 0 ? (
        <section className="card">
          <h2>{tanpaPeran.length} pekerja belum dapat masuk</h2>
          <p className="muted small">
            Mereka ada di daftar pekerja tetapi belum punya peran. Tanpa peran, aplikasi menolak
            mereka di setiap layar — termasuk layar absen.
          </p>
          <ul className="list">
            {tanpaPeran.map((w) => (
              <li key={w.id} className="list-item">
                <strong>{workerDisplayName(w, daftarAnggota)}</strong>
                {bolehAturPeran ? (
                  <PilihPeran
                    id={`peran-${w.id}`}
                    pilihan={ROLE_CHOICES.map((c) => c.role)}
                    ganti={false}
                    onPilih={(role) => void beriPeran(w.userId, role)}
                  />
                ) : (
                  <p className="muted small">
                    Anda tidak punya izin memberi peran. Minta {roleLabel('WORKFORCE_ADMIN')}{' '}
                    melakukannya.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {bolehTambah ? (
        <section className="card">
          <h2>Tambah pekerja</h2>
          <p className="muted small">
            Pilih anggota organisasi. Akun dan namanya berasal dari HBS — halaman ini menambahkan
            profil pekerjanya di Workforce dan memberinya peran.
          </p>

          {anggota.state.status === 'error' ? (
            <p className="warning">
              Daftar anggota belum dapat dibaca, jadi belum ada yang bisa dipilih. Coba muat ulang.
            </p>
          ) : null}

          {anggota.state.status === 'ready' && kandidat.length === 0 ? (
            /* Dibedakan dari "roster gagal dibaca" di atas: yang ini keadaan normal dan selesai. */
            <p className="muted">Semua anggota organisasi sudah punya profil pekerja.</p>
          ) : null}

          {!bolehAturPeran ? (
            <p className="warning">
              Anda dapat menambahkan pekerja, tetapi tidak dapat memberi peran — dan tanpa peran
              orangnya belum bisa masuk. Minta {roleLabel('WORKFORCE_ADMIN')} memberi perannya
              setelah ini.
            </p>
          ) : null}

          <form onSubmit={(e) => void tambah(e)}>
            <label htmlFor="anggota">Anggota</label>
            <select
              id="anggota"
              value={pilihan}
              onChange={(e) => setPilihan(e.target.value)}
              disabled={kandidat.length === 0}
            >
              <option value="">— pilih —</option>
              {kandidat.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {namaLayak(m.displayName) ?? 'Lengkapi profil'} · {m.membershipRole}
                </option>
              ))}
            </select>

            <label htmlFor="peran">Peran</label>
            <select
              id="peran"
              value={peran}
              onChange={(e) => setPeran(e.target.value as WorkforceRole)}
            >
              {ROLE_CHOICES.map((c) => (
                <option key={c.role} value={c.role}>
                  {c.label}
                </option>
              ))}
            </select>
            {/* Penjelasan peran yang sedang dipilih — bukan daftar kelimanya sekaligus, yang
                menuntut orang membaca semuanya untuk menemukan satu yang relevan. */}
            <p className="muted small">{ROLE_CHOICES.find((c) => c.role === peran)?.hint ?? ''}</p>

            <label htmlFor="nomor">Nomor pegawai (opsional)</label>
            <input
              id="nomor"
              value={nomor}
              onChange={(e) => setNomor(e.target.value)}
              placeholder="mis. EMP-0007"
            />

            <label htmlFor="jabatan">Jabatan (opsional)</label>
            <input
              id="jabatan"
              value={jabatan}
              onChange={(e) => setJabatan(e.target.value)}
              placeholder="mis. Kurir, Staf Gudang"
            />

            <button type="submit" className="button-primary" disabled={!pilihan || mengirim}>
              {mengirim ? 'Menyimpan…' : 'Tambah pekerja'}
            </button>
          </form>
        </section>
      ) : null}

      <section className="card">
        <div className="row space-between">
          <h2>Pekerja</h2>
          <span className="badge">{daftarPekerja.length}</span>
        </div>

        {peranan.state.status === 'error' ? (
          <p className="warning">
            Daftar peran belum dapat dibaca, jadi peran di bawah tidak ditampilkan. Ini tidak
            berarti perannya hilang.
          </p>
        ) : null}

        {daftarPekerja.length === 0 ? (
          <EmptyState
            title="Belum ada pekerja"
            hint="Absensi tidak akan muncul di dashboard sampai orangnya ditambahkan di sini."
          />
        ) : (
          <ul className="list">
            {daftarPekerja.map((w) => {
              const keterangan = [w.employeeNumber, w.jobTitle].filter(Boolean).join(' · ');
              const miliknya = rolesOf(daftarPeran, w.userId);
              const bisaDitambah = assignableRoles(daftarPeran, w.userId);
              return (
                <li key={w.id} className="list-item">
                  <div className="row space-between">
                    <strong>{workerDisplayName(w, daftarAnggota)}</strong>
                    <span className="badge">{employmentStatusLabel(w.employmentStatus)}</span>
                  </div>
                  {/*
                    Baris keterangan HANYA muncul bila ada isinya. Nomor pegawai dan jabatan
                    memang opsional, jadi "Tanpa keterangan" bukan informasi — ia kalimat yang
                    memakan ruang untuk memberi tahu bahwa tidak ada yang perlu diberitahukan.
                  */}
                  {keterangan ? <p className="muted small">{keterangan}</p> : null}
                  {peranan.state.status === 'ready' ? (
                    <p className="small">
                      {miliknya.length > 0
                        ? miliknya.map(roleLabel).join(' · ')
                        : 'Tanpa peran — belum dapat masuk'}
                    </p>
                  ) : null}
                  {/*
                    Pemilih peran mengambil satu baris penuh saat TERBUKA — ia memuat select dan
                    tombol batal. Saat tertutup, tautannya berbaris dengan tombol status: ketiganya
                    tindakan atas orang yang sama, dan memisahkannya ke dua baris membuat setiap
                    pekerja setinggi satu layar ponsel.
                  */}
                  {bolehAturPeran &&
                  peranan.state.status === 'ready' &&
                  bisaDitambah.length > 0 &&
                  ubahPeranUntuk === w.id ? (
                    <PilihPeran
                      id={`ubah-peran-${w.id}`}
                      pilihan={bisaDitambah}
                      ganti={miliknya.length > 0}
                      onPilih={(role) => {
                        setUbahPeranUntuk(null);
                        void beriPeran(w.userId, role);
                      }}
                      onBatal={() => setUbahPeranUntuk(null)}
                    />
                  ) : (
                    <div className="row">
                      {bolehAturPeran &&
                      peranan.state.status === 'ready' &&
                      bisaDitambah.length > 0 ? (
                        <button
                          type="button"
                          className="button-link"
                          onClick={() => setUbahPeranUntuk(w.id)}
                        >
                          {miliknya.length > 0 ? 'Ubah peran' : 'Beri peran'}
                        </button>
                      ) : null}
                      {bolehTambah
                        ? (['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const)
                            .filter((s) => s !== w.employmentStatus)
                            .map((s) => (
                              <button
                                key={s}
                                type="button"
                                className="button-link"
                                onClick={() => void ubahStatus(w.id, s)}
                              >
                                Jadikan {employmentStatusLabel(s).toLowerCase()}
                              </button>
                            ))
                        : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}

/**
 * Pemilih peran yang langsung mengirim saat dipilih.
 *
 * Nilainya tidak disimpan sebagai state: setelah dikirim, daftar peran dimuat ulang dan pilihan
 * yang baru saja diberikan hilang dari daftar. Menyimpan pilihan akan menampilkan peran yang tidak
 * ada lagi di dalamnya.
 */
function PilihPeran({
  id,
  pilihan,
  ganti,
  onPilih,
  onBatal,
}: {
  readonly id: string;
  readonly pilihan: readonly WorkforceRole[];
  /** Benar bila orang ini SUDAH punya peran — memilih di sini menggantinya, bukan menambah. */
  readonly ganti: boolean;
  readonly onPilih: (role: WorkforceRole) => void;
  /** Null berarti pemilih ini memang selalu tampil (kartu "belum dapat masuk"). */
  readonly onBatal?: (() => void) | undefined;
}): ReactNode {
  return (
    <div className="row">
      <label htmlFor={id} className="muted small">
        {ganti ? 'Ubah peran' : 'Beri peran'}
      </label>
      <select
        id={id}
        value=""
        onChange={(e) => {
          if (e.target.value) onPilih(e.target.value as WorkforceRole);
        }}
      >
        <option value="">— pilih —</option>
        {pilihan.map((role) => (
          <option key={role} value={role}>
            {roleLabel(role)}
          </option>
        ))}
      </select>
      {onBatal ? (
        <button type="button" className="button-link" onClick={onBatal}>
          Batal
        </button>
      ) : null}
    </div>
  );
}

/**
 * Mengundang orang LANGSUNG dari Workforce.
 *
 * Keberatan Owner 19 Agustus 2026: "kalau bukan marketing, kenapa harus lewat CRM dulu."
 * Yang wajib bukan CRM melainkan identitas HBS — dan satu-satunya alasan CRM terasa wajib adalah
 * layar undangan hanya ada di sana. Layar ini menutup celah itu tanpa Workforce belajar membuat
 * akun: undangannya tetap diterbitkan HBS.
 */
function UndangAnggota({ onSelesai }: { readonly onSelesai: () => void }): ReactNode {
  const session = useSession();
  const [email, setEmail] = useState('');
  const [peran, setPeran] = useState<MembershipRole>('member');
  const [mengirim, setMengirim] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);
  const [tautan, setTautan] = useState<string | null>(null);
  const [disalin, setDisalin] = useState(false);

  async function undang(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const masalah = inviteProblem(email);
    if (masalah) {
      setGalat(masalah);
      return;
    }
    setMengirim(true);
    setGalat(null);
    setTautan(null);
    setDisalin(false);
    try {
      const hasil = await inviteMember(session.auth.accessToken, session.me.organizationId, {
        email: email.trim(),
        membershipRole: peran,
      });
      setTautan(buildAcceptUrl(window.location.origin, hasil.token));
      setEmail('');
      onSelesai();
    } catch (sebab) {
      setGalat(sebab instanceof HbsError ? sebab.message : 'Tidak dapat menghubungi HBS.');
    } finally {
      setMengirim(false);
    }
  }

  return (
    <section className="card">
      <h2>Undang orang baru</h2>
      <p className="muted small">
        Akun dan keanggotaan tetap dibuat HBS — halaman ini hanya menghemat perjalanan ke aplikasi
        lain. Email undangan terkirim otomatis.
      </p>

      {galat ? <p role="alert">{galat}</p> : null}

      {tautan ? (
        <div className="card ok">
          <p>Undangan terkirim. Email otomatis mengarah ke aplikasi CRM.</p>
          {/*
            Tautan ini mendarat di WORKFORCE. Bagikan lewat WhatsApp kepada orang yang tidak akan
            pernah memakai CRM — itulah seluruh maksud layar ini.
            Ditampilkan SEKALI: token dikembalikan HBS sekali dan tidak dapat diambil lagi.
          */}
          <p className="small">
            Untuk kurir dan staf lapangan, kirimkan tautan ini sebagai gantinya — ia mendarat di
            Workforce, bukan di CRM:
          </p>
          <p className="small" style={{ wordBreak: 'break-all' }}>
            {tautan}
          </p>
          <button
            type="button"
            className="button-link"
            onClick={() => {
              void navigator.clipboard?.writeText(tautan).then(() => setDisalin(true));
            }}
          >
            {disalin ? 'Tersalin' : 'Salin tautan'}
          </button>
          <p className="muted small">
            Tautan ini hanya ditampilkan sekali — salin sekarang. Bila hilang, undang ulang.
          </p>
        </div>
      ) : null}

      <form onSubmit={(e) => void undang(e)}>
        <label htmlFor="undang-email">Email</label>
        <input
          id="undang-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nama@contoh.com"
        />

        <label htmlFor="undang-peran">Peran organisasi</label>
        <select
          id="undang-peran"
          value={peran}
          onChange={(e) => setPeran(e.target.value as MembershipRole)}
        >
          {MEMBERSHIP_ROLE_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <p className="muted small">
          {MEMBERSHIP_ROLE_CHOICES.find((c) => c.value === peran)?.hint ?? ''}
        </p>

        <button type="submit" className="button-primary" disabled={mengirim}>
          {mengirim ? 'Mengirim…' : 'Kirim undangan'}
        </button>
      </form>

      <p className="muted small">
        Peran Workforce (Kurir, Karyawan, …) diberikan setelah orangnya menerima undangan, lewat
        kartu di bawah.
      </p>
    </section>
  );
}
